import React, { useState, useEffect, useRef } from 'react';
import FunnelBody from './funnelBody';
import CustomButton from '../../customButton';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import AddFunnelSteps from './addFunnelSteps';
import API from '../../../services/api';
import { getSelectedProject } from '../../../redux/projects/actions';
import { useSelector } from 'react-redux';
import DropdownMenuList from '../../dropdownMenuList';
import { parseFunnelData } from './data';
import { buildMobileRequest } from '../../../analyticsWidgets/mobile';
import { mobileWidgets } from '../../../analyticsWidgets';

import './styles.scss';

const FunnelChart = ({ widget, query, projectOverride, portal, isBrand, lastUpdated }) => {
  const [selectedSites, setSelectedSites] = useState([]);
  const [modalFunnelSteps, setModalFunnelSteps] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [previousAppliedSites, setPreviousAppliedSites] = useState([]);
  const generation = useRef(0);
  const lastRequestKey = useRef(null);
  useEffect(() => () => { generation.current += 1; }, []);
  const [showNumbers, setShowNumbers] = useState(true);

  const selectedProject = useSelector(getSelectedProject);
  const project = projectOverride || selectedProject;
  const scope = isBrand || project?.isBrandPortal
    ? { brandPortalId: portal?.id || project?.id } : { projectId: project?.id };
  const filterKey = JSON.stringify([buildMobileRequest(mobileWidgets['mobile-funnel'],
    { ...query, selectedSites }, scope), lastUpdated]);

  const parseSelectedSites = () => {
    if (!selectedSites || selectedSites.length === 0) {
      return {};
    }

    const newSelected = {};
    selectedSites?.forEach((site, idx) => {
      if (site && site.id) {
        newSelected[`step${idx + 1}`] = {
          label: `${site.displayName} - ${site.id}`,
          value: site.id,
          displayName: site.displayName,
          id: site.id,
          stepName: site.stepName || site.displayName
        };
      }
    });
    return newSelected;
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const isEmpty = array => {
    return Array.isArray(array) ? array.some(item => !item.displayName || !item.id) : true;
  };

  const saveButtonDisabled = () => {
    return !modalFunnelSteps || modalFunnelSteps.length < 2 || isEmpty(modalFunnelSteps) || isLoading;
  };

  const editButtonDisabled = () => {
    return !selectedSites || selectedSites.length < 2 || isEmpty(selectedSites) || isLoading;
  };

  const applyButtonDisabled = () => {
    // Only enable if sites changed (date change triggers auto-fetch)
    return !selectedSites || selectedSites.length < 2 || isEmpty(selectedSites) || isLoading || !checkIfSitesChanged();
  };

  const checkIfSitesChanged = () => {
    if (selectedSites.length !== previousAppliedSites.length) {
      return true;
    }
    for (let i = 0; i < selectedSites.length; i++) {
      if (
        selectedSites[i].id !== previousAppliedSites[i].id ||
        selectedSites[i].stepName !== previousAppliedSites[i].stepName
      ) {
        return true;
      }
    }
    return false;
  };

  const fetchFunnelData = (sites, startDate, endDate) => {
    setIsLoading(true);
    const request = buildMobileRequest(mobileWidgets['mobile-funnel'], {
      ...query, selectedSites: sites, startDate, endDate,
      timezone: query?.timezone || project?.preferences?.timezone
    }, scope);
    const requestGeneration = ++generation.current;
    lastRequestKey.current = filterKey;
    if (!request) { setData([]); setIsLoading(false); return; }

    API.getAnalyticsViewData(mobileWidgets['mobile-funnel'].viewId, request)
      .then(res => {
        if (generation.current !== requestGeneration) return;
        setData(parseFunnelData(res, sites));
        setIsLoading(false);
        setPreviousAppliedSites(sites);
      })
      .catch(error => {
        if (generation.current !== requestGeneration) return;
        console.error(error);
        setData([]);
        setIsLoading(false);
        setPreviousAppliedSites(sites);
      });
  };

  // Refetch the applied ordered steps for every payload-affecting filter change.
  useEffect(() => {
    if (selectedSites.length >= 2 && !isEmpty(selectedSites) && lastRequestKey.current !== filterKey) {
      fetchFunnelData(selectedSites, query?.startDate, query?.endDate);
    }
  }, [filterKey, selectedSites]);

  const toggleShowNumbers = () => {
    setShowNumbers(!showNumbers);
  };

  return (
    <>
      <div className="funnel-container">
        <div className="card">
          <div className="card-header">
            <h5>{widget.title}</h5>
            <div className="widget-action-buttons">
              <info-box widget-title={widget.title} host-id="widget-funnel" text={widget.info.text} />
              <DropdownMenuList
                onToggle={close => (
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      toggleShowNumbers();
                      if (typeof close === 'function') close();
                    }}
                  >
                    Toggle Numbers
                  </button>
                )}
                alignRight={true}
              />
            </div>
          </div>
          <div className="card-body">
            <FunnelBody
              isLoading={isLoading}
              showNumbers={showNumbers}
              data={data}
              onAdd={() => setIsModalOpen(true)}
              onRemove={() => {
                setModalFunnelSteps([]);
                setSelectedSites([]);
              }}
              steps={modalFunnelSteps}
            />
          </div>
          <div className="card-footer">
            {data && data.length > 0 && (
              <CustomButton
                title={'Edit Steps'}
                classes={'btn btn-secondary'}
                loading={isLoading}
                disabled={editButtonDisabled()}
                handleClick={toggleModal}
              />
            )}
            <CustomButton
              title={'Apply'}
              classes={'btn btn-primary'}
              loading={isLoading}
              disabled={applyButtonDisabled()}
              handleClick={() => fetchFunnelData(selectedSites, query?.startDate, query?.endDate)}
            />
          </div>
        </div>
      </div>
      <Modal className="custom-widget-modal" isOpen={isModalOpen} fade={false} toggle={toggleModal} centered>
        <ModalHeader className="header-gray" toggle={toggleModal} tag="div">
          <h2>Funnel Steps</h2>
          <div className="modal-subtitle">
            Add between 2 and 5 steps to your funnel. If no name is set, it will be the site name.
          </div>
        </ModalHeader>
        <ModalBody>
          {isModalOpen && (
            <AddFunnelSteps
              onSelectionChange={setModalFunnelSteps}
              sites={parseSelectedSites()}
              availableSites={query?.selectedSites || []}
              steps={modalFunnelSteps}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <CustomButton title="Cancel" classes="btn-secondary" handleClick={toggleModal} />
          <CustomButton
            title="Save"
            classes="btn-primary"
            handleClick={() => {
              setSelectedSites(modalFunnelSteps);
              toggleModal();
            }}
            disabled={saveButtonDisabled()}
          />
        </ModalFooter>
      </Modal>
    </>
  );
};

export default FunnelChart;
